class NavelisPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    this.status = this.querySelector('[data-predictive-status]');
    this.clearButton = this.querySelector('[data-predictive-clear]');
    this.abortController = null;
    this.debounceTimer = null;
  }

  connectedCallback() {
    if (!this.input || !this.results) return;
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.results.addEventListener('keydown', (event) => this.onResultsKeydown(event));
    this.input.addEventListener('focus', () => this.onFocus());
    this.clearButton?.addEventListener('click', () => this.clear());
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });
    this.toggleClear();
  }

  onFocus() {
    if (this.input.value.trim().length < 2) return;
    if (this.results.innerHTML.trim()) this.open();
    else if (!this.abortController && !this.debounceTimer) this.onInput();
  }

  onInput() {
    this.close();
    this.results.replaceChildren();
    this.toggleClear();
    const term = this.input.value.trim();
    if (term.length < 2) {
      this.abortController?.abort();
      this.results.replaceChildren();
      this.close();
      return;
    }
    this.debounceTimer = window.setTimeout(() => {
      this.debounceTimer = null;
      this.fetchResults(term);
    }, 250);
  }

  async fetchResults(term) {
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.setStatus(this.dataset.loadingText || 'Loading');
    this.setAttribute('aria-busy', 'true');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}search/suggest`, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'query,product,collection,page,article');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', 'predictive-search');

      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Predictive search failed');
      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (controller.signal.aborted || this.abortController !== controller || this.input.value.trim() !== term) return;
      const content = markup.querySelector('[data-predictive-content]');
      if (!content) throw new Error('Predictive search response was incomplete');

      this.results.replaceChildren(content);
      this.setStatus(content.dataset?.predictiveCountLabel || content.querySelectorAll('[role="option"]').length.toString());
      this.open();
    } catch (error) {
      if (controller.signal.aborted || this.abortController !== controller || error.name === 'AbortError') return;
      this.results.innerHTML = `<p class="predictive-search__empty">${this.input.dataset.errorLabel || ''}</p>`;
      this.setStatus(this.input.dataset.errorLabel || '');
      this.open();
    } finally {
      if (this.abortController === controller) {
        this.removeAttribute('aria-busy');
        this.abortController = null;
      }
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'ArrowDown' || this.input.getAttribute('aria-expanded') !== 'true') return;
    const firstResult = this.results.querySelector('a');
    if (firstResult) {
      event.preventDefault();
      firstResult.focus();
    }
  }

  onResultsKeydown(event) {
    const links = [...this.results.querySelectorAll('a')];
    const index = links.indexOf(document.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      this.input.focus();
      this.close();
    } else if (event.key === 'ArrowDown' && links.length) {
      event.preventDefault();
      links[(index + 1) % links.length].focus();
    } else if (event.key === 'ArrowUp' && links.length) {
      event.preventDefault();
      if (index <= 0) this.input.focus();
      else links[index - 1].focus();
    }
  }

  open() {
    this.input.setAttribute('aria-expanded', 'true');
    this.results.hidden = false;
  }

  close() {
    window.clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
    this.abortController?.abort();
    this.abortController = null;
    this.removeAttribute('aria-busy');
    this.setStatus('');
    this.input.setAttribute('aria-expanded', 'false');
    this.results.hidden = true;
  }

  clear() {
    this.input.value = '';
    this.results.replaceChildren();
    this.close();
    this.toggleClear();
    this.input.focus();
  }

  toggleClear() {
    if (this.clearButton) this.clearButton.hidden = this.input.value.length === 0;
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', NavelisPredictiveSearch);
